import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Cookie from "js-cookie";
import get from "lodash.get";
import { useDispatch } from "react-redux";
import { OutlinedButton, ToastError } from "@bloomberg/media-ui-core";
import { ButtonDropdown, DropdownOption } from "@bloomberg/media-ui-dropdown";
import { Translate } from "@bloomberg/media-ui-icons";
import { SubscriberOnlyBenefitOverlay } from "@bloomberg/media-ui-subscriptions";
import { TipKey, TipVariant, useTippy } from "@bloomberg/tippy";
import { useAbba } from "@foundation/abba-client";
import { useRegUIClient } from "@foundation/reg-ui-client";

import { AppErrorReason } from "~errors/errors";
import { updateLanguagePreference } from "~redux/features/translation-slice";
import { Language, TRANSLATION_LANGUAGES } from "~types/types";
import { Tracker } from "~utils/tracker";

import styles from "./styles.module.css";

interface LanguagePreferenceProps {
    sourceLanguage: Language;
    targetLanguage?: TRANSLATION_LANGUAGES;
    translationError?: string;
}

interface LanguageOption {
    value: string;
    displayValue: string;
}

const LANGUAGE_OPTIONS: LanguageOption[] = [
    {
        value: "EN",
        displayValue: "English",
    },
    {
        value: "FR",
        displayValue: "Français",
    },
    {
        value: "DE",
        displayValue: "Deutsch",
    },
    {
        value: "ES",
        displayValue: "Español",
    },
    {
        value: "PT",
        displayValue: "Português",
    },
    {
        value: "IT",
        displayValue: "Italiano",
    },
    {
        value: "JA",
        displayValue: "日本語",
    },
    {
        value: "KO",
        displayValue: "한국어",
    },
    {
        value: "ZH",
        displayValue: "简体中文",
    },
    {
        value: "ZT",
        displayValue: "繁體中文",
    },
];

const HUMAN_READABLE_TRANSLATION_ERROR_MESSAGE: { [error: string]: string } = {
    [AppErrorReason.INVALID_TRANSLATION]:
        "Sorry, translation is unavailable for this article.",
    [AppErrorReason.TRANSLATION_RATE_LIMITED]:
        "We couldn't translate this article at the moment. Please try again later.",
};

const translationTracker = new Tracker({
    event: "translation_event",
    category: "engagement",
});

const getTranslationErrorMessage = (error?: AppErrorReason) => {
    if (!error) return undefined;

    return HUMAN_READABLE_TRANSLATION_ERROR_MESSAGE[error];
};

// TODO: [ENG4WCP-5161] add tests and update styles to tailwind syntax
const LanguagePreference: React.FC<LanguagePreferenceProps> = ({
    sourceLanguage,
    targetLanguage,
    translationError,
}) => {
    const dispatch = useDispatch();
    const { client } = useRegUIClient();
    const [languagePreference, setLanguagePreference] =
        useState<TRANSLATION_LANGUAGES | null>(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const translateButtonRef = useRef<HTMLButtonElement>(null);
    const { abbaConfig, accesslistMembership } = useAbba();
    const enabledByAbba = get(abbaConfig, ["translations", "enableArticles"]);

    const [translateElement, setTranslateElement] = useState<Element | null>(
        null
    );
    useTippy({
        tipKey: TipKey.TRANSLATE,
        variant: TipVariant.POPOVER,
        refNode: translateElement,
    });

    useEffect(() => {
        Cookie.remove("_language-preference");

        if (!targetLanguage) return;

        setLanguagePreference(targetLanguage);
        dispatch(updateLanguagePreference(targetLanguage));
    }, []);

    useEffect(() => {
        if (!translationError || loading) return;

        translationTracker.trackEvent({
            action: "error",
            label: translationError.toLowerCase(),
        });
    }, [translationError, loading]);

    const originalLanguage = useMemo(
        () => sourceLanguage.toUpperCase(),
        [sourceLanguage]
    );

    const onLanguageSelect = useCallback((v?: DropdownOption | null) => {
        if (!v) {
            return;
        }

        setLoading(true);

        Cookie.set("_language-preference", v.value as TRANSLATION_LANGUAGES);

        if (v.value !== originalLanguage) {
            translationTracker.trackEvent({
                action: "click",
                label: `translate_to_${v.value}`,
            });
        } else {
            Cookie.remove("_language-preference");
        }

        // @ts-expect-error firefox accepts 'true' to force reload
        window.location.reload(true);
    }, []);

    const nonsubscriberClick = useCallback(() => {
        translationTracker.trackEvent({
            action: "click",
            label: "nonsub",
        });

        setIsOpen(true);
    }, []);

    const readInOriginalLanguage = useCallback(() => {
        translationTracker.trackEvent({
            action: "click",
            label: `read_in_${originalLanguage}`,
        });

        onLanguageSelect({ value: originalLanguage });
    }, [originalLanguage]);

    const options = useMemo(
        () =>
            LANGUAGE_OPTIONS.filter(
                ({ value }) =>
                    value !== languagePreference &&
                    !(!languagePreference && value === originalLanguage)
            ).map((option) => {
                if (option?.value !== originalLanguage) {
                    return option;
                }

                return {
                    value: option.value,
                    displayValue: `${option.displayValue} (Original)`,
                };
            }),
        [languagePreference, originalLanguage]
    );

    if (!client) {
        return null;
    }

    // now assured to be on the client
    const isPreviewPage = window.location.href.includes("/preview/");

    if (isPreviewPage) {
        return null;
    }

    // restrict to english content for now
    if (sourceLanguage !== "en") {
        return null;
    }

    // not enabled via abba or on an allowed network
    if (!enabledByAbba && !accesslistMembership) {
        return null;
    }

    // if you haven't interacted with the feature or aren't a subscriber,
    // show the restricted button
    if (
        !languagePreference &&
        !accesslistMembership &&
        !(client.isBBGSubscriber() || client.isBWSubscriber())
    ) {
        return (
            <div>
                <OutlinedButton
                    ref={translateButtonRef}
                    version="phoenix"
                    size="small"
                    unstyled
                    className={styles.readInEnglish}
                    loading={loading}
                    onClick={nonsubscriberClick}
                >
                    <div className={styles.readInEnglishContent}>
                        <Translate />
                        Translate
                    </div>
                </OutlinedButton>
                {isOpen && (
                    <SubscriberOnlyBenefitOverlay
                        client={client as any}
                        anchorElementRef={translateButtonRef}
                        inSource="article-translation"
                        onExit={() => setIsOpen(false)}
                    >
                        Bloomberg subscribers can translate articles into
                        multiple languages.
                    </SubscriberOnlyBenefitOverlay>
                )}
            </div>
        );
    }

    if (
        loading ||
        (languagePreference && languagePreference !== originalLanguage)
    ) {
        return (
            <div>
                <OutlinedButton
                    version="phoenix"
                    size="small"
                    unstyled
                    className={styles.readInEnglish}
                    onClick={readInOriginalLanguage}
                    loading={loading}
                >
                    <div className={styles.readInEnglishContent}>
                        <Translate />
                        Read in{" "}
                        {LANGUAGE_OPTIONS.find(
                            ({ value }) => value === originalLanguage
                        )?.displayValue || "English"}
                    </div>
                </OutlinedButton>
            </div>
        );
    }

    return (
        <>
            {Boolean(translationError) && (
                <ToastError
                    open={!loading}
                    text={getTranslationErrorMessage(
                        translationError as AppErrorReason
                    )}
                />
            )}
            <ButtonDropdown
                defaultOption={{
                    value: "",
                    displayValue: "Translate",
                }}
                options={options}
                onSelect={onLanguageSelect}
                className={styles.languagePreferenceSelector}
                icon={<Translate />}
                ref={setTranslateElement}
            />
        </>
    );
};

export default LanguagePreference;
